import { MatchListItemFragment } from '@amf/shared/types/graphql';
import { useSWRInfinite } from 'swr';
import { useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import Button from '@amf/shared/components/button/Button';

export const LIMIT = 5;

const getKey = (id: string) => (offset: number, previous: MatchListItemFragment[] | null) => {
  const params = new URLSearchParams({ offset: `${LIMIT * offset}`, id });
  if (previous === null || offset === 0) return `/api/match/previous?${params}`;
  if (previous.length < LIMIT) return null;
  return `/api/match/previous?${params}`;
};

function usePreviousMatches(id: string) {
  const { t } = useTranslate();
  const { data, size, setSize, error } = useSWRInfinite<MatchListItemFragment[]>(getKey(id));

  const matches = useMemo<MatchListItemFragment[]>(() => {
    if (!data || !Array.isArray(data)) return [];
    return [].concat(...data);
  }, [data]);

  const loadMore = useCallback(() => {
    if (!matches || data === undefined) return null;
    if (size > data.length) {
      return (
        <Button type='button' variant='dark-outline' disabled>
          {t('match.loading')}
        </Button>
      );
    }

    if (LIMIT * size > matches.length) return null;
    return (
      <Button type='button' variant='dark-outline' onClick={() => setSize(size + 1)}>
        {t('match.more')}
      </Button>
    );
  }, [data, matches, setSize, size, t]);

  return {
    matches,
    loading: !data && !error,
    loadMore,
    isLoadMore: !(!matches || data === undefined || LIMIT * size > matches.length),
  };
}

export default usePreviousMatches;
